import React, { useEffect, useState } from 'react';
import { getTrades } from '../api';

const COMMISSION_PER_RT = 5.82;
const FEE_BREAKDOWN = [
  { label: 'Tradovate Platform Fee', amount: 1.29, per: 'side', note: 'Free plan, non-micro contracts' },
  { label: 'CME Exchange Fee',       amount: 1.50, per: 'side', note: 'Chicago Mercantile Exchange' },
  { label: 'NFA Fee',                amount: 0.02, per: 'side', note: 'National Futures Association' },
  { label: 'Clearing Fee',           amount: 0.10, per: 'side', note: 'Estimated clearing/settlement' },
];
const TOTAL_PER_SIDE = FEE_BREAKDOWN.reduce((s, f) => s + f.amount, 0);
const TOTAL_RT = TOTAL_PER_SIDE * 2;

export default function Commissions() {
  const [trades, setTrades] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getTrades().then(r => {
      const closed = (r.data?.trades || []).filter((t: any) => t.status === 'closed' && t.broker === 'tradovate');
      setTrades(closed);
      setLoading(false);
    });
  }, []);

  const totalGross = trades.reduce((s, t) => s + (t.gross_pnl ?? t.pnl ?? 0), 0);
  const totalCommission = trades.reduce((s, t) => s + (t.commission_usd ?? COMMISSION_PER_RT * (t.quantity || 1)), 0);
  const totalNet = totalGross - totalCommission;
  const commissionPct = totalGross !== 0 ? (totalCommission / Math.abs(totalGross)) * 100 : 0;
  const avgCommission = trades.length > 0 ? totalCommission / trades.length : 0;
  const upgradeBreakEven = Math.ceil((49 / (COMMISSION_PER_RT - 4.02)));

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-xl font-bold">Commissions</h1>
        <p className="text-xs text-gray-500 mt-0.5">Fee tracking. Tradovate commission costs per trade and cumulative impact on net P&L.</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Commission', value: `$${totalCommission.toFixed(2)}`, cls: 'text-orange-400' },
          { label: 'Total Gross P&L',  value: `$${totalGross >= 0 ? '+' : ''}${totalGross.toFixed(2)}`, cls: totalGross >= 0 ? 'text-green-400' : 'text-red-400' },
          { label: 'Total Net P&L',    value: `$${totalNet >= 0 ? '+' : ''}${totalNet.toFixed(2)}`,    cls: totalNet >= 0 ? 'text-green-400' : 'text-red-400' },
          { label: 'Avg Commission',   value: `$${avgCommission.toFixed(2)}`, cls: 'text-orange-400' },
          { label: 'Commission / Gross', value: `${commissionPct.toFixed(1)}%`, cls: 'text-gray-300' },
          { label: 'Trades Tracked',  value: trades.length.toString(), cls: 'text-gray-300' },
        ].map(({ label, value, cls }) => (
          <div key={label} className="bg-gray-900 border border-gray-800 rounded-lg p-4 text-center">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">{label}</div>
            <div className={`text-lg font-bold font-mono ${cls}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Fee breakdown */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
        <h3 className="font-semibold mb-4">Tradovate Fee Structure — MNQ Futures (Free Plan)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left pb-2">Fee Component</th>
                <th className="text-right pb-2">Per Side</th>
                <th className="text-right pb-2">Round Trip</th>
                <th className="text-left pb-2 pl-4">Notes</th>
              </tr>
            </thead>
            <tbody>
              {FEE_BREAKDOWN.map(f => (
                <tr key={f.label} className="border-b border-gray-800">
                  <td className="py-2 font-medium">{f.label}</td>
                  <td className="py-2 text-right font-mono text-orange-400">${f.amount.toFixed(2)}</td>
                  <td className="py-2 text-right font-mono text-orange-400">${(f.amount * 2).toFixed(2)}</td>
                  <td className="py-2 pl-4 text-xs text-gray-500">{f.note}</td>
                </tr>
              ))}
              <tr className="border-t border-gray-600">
                <td className="py-2 font-bold">Total (per contract)</td>
                <td className="py-2 text-right font-mono font-bold text-white">${TOTAL_PER_SIDE.toFixed(2)}</td>
                <td className="py-2 text-right font-mono font-bold text-white">${TOTAL_RT.toFixed(2)}</td>
                <td className="py-2 pl-4 text-xs text-gray-500">Per MNQ contract round trip</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-gray-800 rounded p-4 text-xs text-gray-400 space-y-1">
          <div className="font-semibold text-gray-300 mb-2">Upgrade Analysis</div>
          <div>Starter plan ($49/mo): ~$4.02/RT → saves ${(TOTAL_RT - 4.02).toFixed(2)}/trade</div>
          <div>Break-even at Starter: <span className="text-white font-mono">{upgradeBreakEven} trades/month</span></div>
          <div>Unlimited plan ($99/mo): ~$3.26/RT → saves ${(TOTAL_RT - 3.26).toFixed(2)}/trade (break-even: {Math.ceil(99 / (TOTAL_RT - 3.26))} trades/mo)</div>
          <div className="text-yellow-400 mt-2">At current volume, Free plan is likely optimal. Revisit when hitting 15+ trades/month consistently.</div>
        </div>
      </div>

      {/* Per-trade commission log */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-semibold">Commission Log</h3>
          <p className="text-xs text-gray-500 mt-1">Per-trade breakdown of gross P&L, commission, and net P&L</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Instrument</th>
                <th className="text-left p-3">Class</th>
                <th className="text-left p-3">Qty</th>
                <th className="text-right p-3">Entry</th>
                <th className="text-right p-3">Exit</th>
                <th className="text-right p-3">Gross P&L</th>
                <th className="text-right p-3">Commission</th>
                <th className="text-right p-3">Net P&L</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 ? (
                <tr><td colSpan={9} className="text-center text-gray-600 p-8">No closed live trades yet</td></tr>
              ) : trades.map((t: any) => {
                const gross = t.gross_pnl ?? t.pnl ?? 0;
                const comm = t.commission_usd ?? COMMISSION_PER_RT * (t.quantity || 1);
                const net = t.net_pnl ?? (gross - comm);
                return (
                  <tr key={t.id} className="border-b border-gray-800 hover:bg-gray-800">
                    <td className="p-3 text-gray-400 text-xs">{new Date(t.close_time || t.created_at).toLocaleDateString()}</td>
                    <td className="p-3 font-medium">{t.instrument}</td>
                    <td className="p-3 text-xs text-gray-400">{t.signal_class || '—'}</td>
                    <td className="p-3 font-mono">{t.quantity || 1}</td>
                    <td className="p-3 font-mono text-xs text-right">{t.entry_price || '—'}</td>
                    <td className="p-3 font-mono text-xs text-right">{t.exit_price || '—'}</td>
                    <td className={`p-3 font-mono font-bold text-right ${gross >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${gross >= 0 ? '+' : ''}{gross.toFixed(2)}
                    </td>
                    <td className="p-3 font-mono text-right text-orange-400">-${comm.toFixed(2)}</td>
                    <td className={`p-3 font-mono font-bold text-right ${net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      ${net >= 0 ? '+' : ''}{net.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {trades.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-700 bg-gray-800">
                  <td colSpan={6} className="p-3 text-xs text-gray-500 uppercase font-bold">Totals</td>
                  <td className={`p-3 font-mono font-bold text-right ${totalGross >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${totalGross >= 0 ? '+' : ''}{totalGross.toFixed(2)}
                  </td>
                  <td className="p-3 font-mono font-bold text-right text-orange-400">-${totalCommission.toFixed(2)}</td>
                  <td className={`p-3 font-mono font-bold text-right ${totalNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    ${totalNet >= 0 ? '+' : ''}{totalNet.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
